/**
 * Public Route Component
 * For auth pages (login, register) - redirects authenticated users
 * Special case: allows authenticated users to access login page for remember-me functionality
 */

import { type ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@shared/contexts'
import { LoadingPage } from '@shared/components/feedback'

interface PublicRouteProps {
  children: ReactNode
}

export function PublicRoute({ children }: PublicRouteProps) {
  const { isAuthenticated, isLoading, authChecked, refetchUser } = useAuth()
  const location = useLocation()
  const [validated, setValidated] = useState(false)

  // isAuthenticated is seeded from localStorage and can be stale (e.g. an
  // expired session cookie with leftover user/token data). Before bouncing
  // an auth page away, confirm the session is actually still valid — a false
  // positive here would otherwise strand users unable to reach /register.
  const needsValidation = isAuthenticated && location.pathname !== '/login'

  useEffect(() => {
    if (needsValidation && !validated) {
      void refetchUser().finally(() => setValidated(true))
    }
  }, [needsValidation, validated, refetchUser])

  // Show loading while checking authentication
  if (isLoading || !authChecked || (needsValidation && !validated)) {
    return <LoadingPage />
  }

  // Allow authenticated users to view login page (for remember-me settings)
  // But redirect from register and password recovery pages
  if (isAuthenticated && location.pathname !== '/login') {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
